/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import { BaseContentController } from './base-content-controller';
import { generatePlayErrorElem } from './content-error-overlay';

/**
 * @import { ContentController } from "."
 * @import { Content } from '../content'
 */

/**
 * @implements {ContentController}
 */
export default class RegionContentController extends BaseContentController {
  /**
   * @param {Content} content
   * @param {ContentController} contentController
   * @param {number} duration
   * @param {HTMLElement} parentEl
   */
  constructor(content, contentController, duration, parentEl) {
    super();

    this.content = content;
    this.contentController = contentController;
    this.duration = duration;
    this.parentEl = parentEl;

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    this.durationTimeout = undefined;
    /** @type {(() => void) | undefined} */
    this.removePictogram = undefined;
    this.started = false;
    this.preloaded = false;

    this.handleError = this.handleError.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onError = this.onError.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
  }

  /**
   * @private
   * @param {Error} err
   */
  handleError(err) {
    const errorEl = generatePlayErrorElem(
      /** @type {Error} */ (err),
      this.content
    );
    this.parentEl.appendChild(errorEl);
    this.removePictogram = () => {
      this.parentEl.removeChild(errorEl);
      this.removePictogram = undefined;
    };
  }

  /**
   * Reakce na konec obsahu.
   */
  onEnded() {
    this.restart();
  }

  /**
   * Reakce na chybu při přehrávání.
   * @private
   */
  onError() {
    this.restart();
  }

  /**
   * @param {Content} content
   * @returns {void}
   */
  onPlayed(content) {
    this.emitter.emit('played', content);
  }

  /**
   * @param {Content} content
   * @returns {void}
   */
  onPlaying(content) {
    this.emitter.emit('playing', content);
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(async () => {
      this.contentController.on('ended', this.onEnded);
      this.contentController.on('error', this.onError);
      this.contentController.on('played', this.onPlayed);
      this.contentController.on('playing', this.onPlaying);
      await this.contentController
        .preload()
        .then(() => (this.preloaded = true))
        .catch(() => {});
    });
  }

  /** @type {ContentController['restart']} */
  async restart() {
    await super.restartCallback(async () => {
      clearTimeout(this.durationTimeout);
      this.removePictogram?.();
      if (!this.started) {
        // První volání start() neuspělo, tak je nutné provést preload a start znovu.
        await this.startContentController(0);
        return;
      }
      // První volání start() uspělo, tak je možné jen restartovat.
      await this.contentController.restart().catch(this.handleError);
      this.setDurationTimer();
    });
  }

  /**
   * @private
   */
  setDurationTimer() {
    if (this.duration === 0) {
      return;
    }
    clearTimeout(this.durationTimeout);
    this.durationTimeout = setTimeout(async () => {
      this.onPlayed(this.content);
      await this.restart();
    }, this.duration);
  }

  /** @type {ContentController['start']} */
  async start() {
    await super.startCallback(async () => {
      await this.startContentController(0);
    });
  }

  /**
   * @private
   * @param {number} offsetMs
   */
  async startContentController(offsetMs) {
    try {
      if (!this.preloaded) {
        await this.contentController.preload();
        this.preloaded = true;
      }
      await this.contentController.start(offsetMs);
      this.started = true;
    } catch (err) {
      this.handleError(/** @type {Error} */ (err));
    } finally {
      // Spuštění loopovacího mechanismusu.
      this.setDurationTimer();
    }
  }

  /** @type {ContentController['stop']} */
  async stop() {
    // Prvně odregistrace událostí, aby po zavolání stop nemohlo dojít k volání restart.
    this.contentController.off('ended', this.onEnded);
    this.contentController.off('error', this.onError);
    this.contentController.off('played', this.onPlayed);
    this.contentController.off('playing', this.onPlaying);

    await super.stopCallback(async () => {
      clearTimeout(this.durationTimeout);
      this.removePictogram?.();
      // Umyslné ignorování chyb.
      await this.contentController.stop().catch(() => {});
      this.preloaded = false;
      this.started = false;
    });
  }
}
